import { useEffect, useRef, useState } from 'react';
import { DEFAULT_TOTAL_RECORDS, MIN_TOTAL_RECORDS } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/constants';
import { createDefaultTargetConfig } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/steps/RecordsConfigStep';

/**
 * Largest-remainder allocation of integer `total` using `weights` (same order
 * as `names`). Zero/empty weights fall back to an even split. Leftover units
 * go to the largest fractional parts; ties keep `names` order.
 */
export const allocateByWeights = (total, names, weights) => {
    if (!names.length || !Number.isFinite(total)) return {};
    const target = Math.max(0, Math.floor(total));
    const safeWeights = names.map((_, index) => {
        const weight = weights[index];
        return Number.isFinite(weight) && weight > 0 ? weight : 0;
    });
    const weightSum = safeWeights.reduce((sum, weight) => sum + weight, 0);
    if (weightSum <= 0) {
        const base = Math.floor(target / names.length);
        const remainder = target % names.length;
        return Object.fromEntries(
            names.map((name, index) => [name, base + (index < remainder ? 1 : 0)])
        );
    }
    const parts = names.map((name, index) => {
        const exact = (safeWeights[index] * target) / weightSum;
        const floor = Math.floor(exact);
        return { name, floor, frac: exact - floor, index };
    });
    const leftover = target - parts.reduce((sum, part) => sum + part.floor, 0);
    const extra = new Set(
        [...parts]
            .sort((left, right) => right.frac - left.frac || left.index - right.index)
            .slice(0, leftover)
            .map((part) => part.name)
    );
    return Object.fromEntries(
        parts.map((part) => [part.name, part.floor + (extra.has(part.name) ? 1 : 0)])
    );
};

/** Split `total` evenly across `names`, spreading the remainder over the first entries. */
export const splitEvenly = (total, names) =>
    allocateByWeights(
        total,
        names,
        names.map(() => 1)
    );

/** Scale existing per-target counts so they still sum to `newTotal`. */
export const scaleCounts = (counts, names, newTotal) =>
    allocateByWeights(
        newTotal,
        names,
        names.map((name) => counts[name] || 0)
    );

/**
 * Keep the total fixed and move the leftover across the other targets
 * (preserving their ratios) when one target count changes.
 */
export const rebalanceCounts = (counts, names, changedName, newValue, total) => {
    if (!Number.isFinite(newValue)) {
        return { ...counts, [changedName]: newValue };
    }
    const nextValue = Math.max(0, Math.floor(newValue));
    const others = names.filter((name) => name !== changedName);
    if (!others.length) {
        return { [changedName]: nextValue };
    }
    if (!Number.isFinite(total)) {
        return { ...counts, [changedName]: nextValue };
    }
    const boundedTotal = Math.max(0, Math.floor(total));
    const clamped = Math.min(nextValue, boundedTotal);
    return {
        [changedName]: clamped,
        ...allocateByWeights(
            boundedTotal - clamped,
            others,
            others.map((name) => counts[name] || 0)
        ),
    };
};

/** Records/counts/configs snapshot applied when a previous configuration is loaded. */
const prefillStateFor = (names, { totalRecords: prefillTotal, counts: prefillCounts, configs: prefillConfigs }) => {
    const hasCounts = names.some((name) => Number.isFinite(prefillCounts?.[name]));
    const countValues = hasCounts
        ? Object.fromEntries(
              names.map((name) => [name, Number.isFinite(prefillCounts?.[name]) ? prefillCounts[name] : 0])
          )
        : null;
    const summed = countValues ? Object.values(countValues).reduce((sum, value) => sum + value, 0) : 0;
    const total = Number.isFinite(prefillTotal) && prefillTotal > 0 ? prefillTotal : summed || DEFAULT_TOTAL_RECORDS;
    return {
        totalRecords: total,
        counts: countValues || splitEvenly(total, names),
        configs: Object.fromEntries(
            names.map((name) => [name, { ...createDefaultTargetConfig(), ...(prefillConfigs?.[name] || {}) }])
        ),
    };
};

/**
 * State for the "Records to generate" grid + per-target config sections
 * (classification / multi-label / NER step 2). Total defaults to 1,000 split
 * evenly across the targets. Editing a target keeps the total and rebalances
 * the others; editing the total scales every target by the same ratio.
 */
export default function useRecordsState(targetNames) {
    const [totalRecords, setTotalRecords] = useState(DEFAULT_TOTAL_RECORDS);
    const [counts, setCounts] = useState({});
    const [configs, setConfigs] = useState({});
    // Typing "1200" over "1,000" emits 1, then 12, then 120. Scale from the
    // mix captured at the start of that edit so integer rounding cannot zero
    // a label and then lock the rest of the digits to a 1:0 split.
    const ratioBaselineRef = useRef(null);
    // Load-previous prefill waiting for the reseed effect when the target list
    // is changing in the same update (setEditedNames + prefill together).
    const pendingPrefillRef = useRef(null);

    const applyState = (next) => {
        ratioBaselineRef.current = null;
        setTotalRecords(next.totalRecords);
        setCounts(next.counts);
        setConfigs(next.configs);
    };

    // Targets load asynchronously (usecase / get-label-counts); (re)seed state
    // whenever the target list changes — from a pending prefill when one
    // matches the new list, otherwise from the defaults.
    useEffect(() => {
        const namesKey = targetNames.join('\u0000');
        const pending = pendingPrefillRef.current;
        pendingPrefillRef.current = null;
        if (pending && pending.namesKey === namesKey) {
            applyState(prefillStateFor(targetNames, pending));
            return;
        }
        ratioBaselineRef.current = null;
        setCounts(splitEvenly(DEFAULT_TOTAL_RECORDS, targetNames));
        setConfigs(Object.fromEntries(targetNames.map((name) => [name, createDefaultTargetConfig()])));
        setTotalRecords(DEFAULT_TOTAL_RECORDS);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetNames.join('\u0000')]);

    /**
     * Apply a loaded previous configuration. `names` is the target list the
     * prefill belongs to: when it differs from the current one (the caller is
     * updating the labels in the same handler), the prefill is applied by the
     * reseed effect once the new list propagates.
     */
    const prefill = ({ names, totalRecords: prefillTotal, counts: prefillCounts, configs: prefillConfigs }) => {
        const nextNames = names?.length ? names : targetNames;
        const entry = {
            namesKey: nextNames.join('\u0000'),
            totalRecords: prefillTotal,
            counts: prefillCounts,
            configs: prefillConfigs,
        };
        if (entry.namesKey === targetNames.join('\u0000')) {
            pendingPrefillRef.current = null;
            applyState(prefillStateFor(nextNames, entry));
            return;
        }
        pendingPrefillRef.current = entry;
    };

    const onTotalRecordsChange = (value) => {
        setTotalRecords(value);
        if (!Number.isFinite(value)) return;
        setCounts((prev) => {
            if (!ratioBaselineRef.current) {
                ratioBaselineRef.current = { ...prev };
            }
            return scaleCounts(ratioBaselineRef.current, targetNames, value);
        });
    };

    const onCountChange = (name, value) => {
        ratioBaselineRef.current = null;
        if (targetNames.length === 1 && Number.isFinite(value)) {
            setTotalRecords(Math.max(0, Math.floor(value)));
        }
        setCounts((prev) => rebalanceCounts(prev, targetNames, name, value, totalRecords));
    };

    const onConfigChange = (name, partial) => {
        setConfigs((prev) => ({ ...prev, [name]: { ...prev[name], ...partial } }));
    };

    const totalRecordsError = !Number.isFinite(totalRecords) || totalRecords < MIN_TOTAL_RECORDS;

    return {
        totalRecords,
        counts,
        configs,
        onTotalRecordsChange,
        onCountChange,
        onConfigChange,
        totalRecordsError,
        prefill,
    };
};
